import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Bug, CheckCircle2, ChevronDown, HelpCircle, Loader2, Mail, Send } from "lucide-react";
import { useState } from "react";
import { MyRequests } from "@/components/support/MyRequests";
import { mySupportRequestsKey } from "@/hooks/support";
import { DashboardLayout } from "@/layouts/DashboardLayout";
import { cn } from "@/lib/utils";
import { api } from "@/services/api";
import type { SupportRequestType } from "@/types";

const TYPE_OPTIONS: { value: SupportRequestType; label: string; description: string }[] = [
  {
    value: "question",
    label: "Question",
    description: "Something unclear about how the app works",
  },
  {
    value: "feature_request",
    label: "Feature request",
    description: "An idea for something we could add",
  },
  {
    value: "other",
    label: "Other",
    description: "General feedback that doesn't fit above",
  },
];

const SUBJECT_MAX = 255;
const MESSAGE_MAX = 5000;

/** Fallback shown only if the FAQ endpoint fails — the canonical list is
 * admin-managed and served by /api/public/faqs (KC-459). */
const FALLBACK_FAQS = [
  {
    q: "When do work packages unlock?",
    a: "Two per week: one released Monday and one Thursday, at 13:00 (UK time). Each stays open for a 3-day window, and you can submit at any point within it.",
  },
  {
    q: "How is my feedback calculated?",
    a: "Quantitative feedback is a percentage: 60% or above is acceptable work, 85% or above is excellent, and 95% or above is truly exceptional. WP2–WP8 are averaged equally; the pass threshold for this cohort is 65%. WP1 is an unweighted practice run and never counts toward your average. A no-show scores 0 and does count, so submit something for every work package.",
  },
  {
    q: "Can I resubmit a work package?",
    a: "You can submit up to three times while the window is open: your first two submissions return AI feedback in about 30 seconds, and your final submission goes for Executive Review sign-off. Submit a day early so you have time to act on the feedback before the window closes.",
  },
  {
    q: "When do I receive my results?",
    a: "Quantitative and qualitative feedback lands about 30 seconds after every submission. The Executive Review and your Relative Feedback are assessed once, after the submission window closes, and you receive them within 48 hours of the deadline.",
  },
  {
    q: "Can I use AI tools?",
    a: "Yes, you're expected to. In a real-world job you would use AI to improve and refine your work, and it will be considerably harder to receive a high percentile if you can't use AI well. We have factored this into our feedback model. The judgement, sourcing choices and final view should still be yours, and any AI use must be disclosed alongside your references.",
  },
  {
    q: "What happens if I miss a deadline?",
    a: "The window is final: a missed work package scores 0 and counts toward your average (WP1 excepted, as it is unweighted). If something serious gets in the way, contact the team as early as you can.",
  },
  {
    q: "What recognition is on offer?",
    a: "Contributors in the top 15% of the cohort earn the Outstanding Analyst badge, and the top 5% go on to earn the Exceptional Analyst badge, both displayed on your public portfolio with a secured digital copy for your CV or LinkedIn. Selected work packages are published by UK Investor Magazine, and some cohorts feature a sponsored internship.",
  },
];

export default function SupportPage() {
  const [type, setType] = useState<SupportRequestType>("question");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{ subject?: string; message?: string }>({});
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const queryClient = useQueryClient();

  const faqsQuery = useQuery({
    queryKey: ["public", "faqs"],
    queryFn: () => api.getPublicFaqs(),
  });
  const faqs = faqsQuery.isError
    ? FALLBACK_FAQS.map((f, i) => ({ id: String(i), question: f.q, answer: f.a }))
    : (faqsQuery.data ?? []);

  const validate = () => {
    const errs: { subject?: string; message?: string } = {};
    if (subject.trim().length === 0) errs.subject = "Subject is required";
    else if (subject.length > SUBJECT_MAX) errs.subject = `Max ${SUBJECT_MAX} characters`;
    if (message.trim().length === 0) errs.message = "Message is required";
    else if (message.length > MESSAGE_MAX) errs.message = `Max ${MESSAGE_MAX} characters`;
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!validate()) return;
    setSubmitting(true);
    try {
      await api.submitSupportRequest({ type, subject: subject.trim(), message: message.trim() });
      setSubmitted(true);
      // Surface the just-submitted request in the "Your requests" list below.
      queryClient.invalidateQueries({ queryKey: mySupportRequestsKey });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setType("question");
    setSubject("");
    setMessage("");
    setError(null);
    setFieldErrors({});
    setSubmitted(false);
  };

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto flex flex-col gap-[22px]">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[.16em] text-content-muted">
            Support
          </p>
          <h1 className="mt-1.5 text-3xl sm:text-4xl font-extrabold tracking-[-.015em] text-content-foreground">
            FAQ and Help
          </h1>
          <p className="mt-1.5 text-[15px] text-[#5C6E63]">
            Quick answers to common questions, and a fast route to a real person when you need one.
          </p>
        </div>

        {/* FAQ */}
        <section className="rounded-[14px] border border-content-border bg-card px-7 py-[26px]">
          <div className="text-[11px] font-bold tracking-[.16em] text-content-muted">FAQ</div>
          <h2 className="mt-1 text-[22px] font-bold tracking-[-.01em] text-card-foreground">
            Frequently asked questions
          </h2>
          <div className="mt-3 flex flex-col">
            {faqsQuery.isPending ? (
              <div className="flex flex-col gap-3 py-4">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="h-5 animate-pulse rounded bg-content-border/40" />
                ))}
              </div>
            ) : (
              faqs.map((f, i) => (
                <div key={f.id} className="border-b border-content-border/60">
                  <button
                    type="button"
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    aria-expanded={openFaq === i}
                    className="flex w-full items-center gap-3.5 px-1 py-4 text-left text-[15px] font-semibold text-content-foreground transition-colors hover:text-[#1E6F4F]"
                  >
                    {f.question}
                    <ChevronDown
                      className={cn(
                        "ml-auto h-[15px] w-[15px] flex-none transition-transform duration-200",
                        openFaq === i && "rotate-180"
                      )}
                      strokeWidth={2}
                    />
                  </button>
                  {openFaq === i && (
                    <div className="max-w-[720px] px-1 pb-[18px] text-sm leading-[1.65] text-[#5C6E63]">
                      {f.answer}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </section>

        {/* Early Tester Guide */}
        <section className="rounded-[14px] border border-content-border bg-card px-7 py-[26px]">
          <div className="flex items-center gap-3.5 mb-4">
            <div className="flex h-[42px] w-[42px] flex-none items-center justify-center rounded-[11px] bg-[#E9F1E6]">
              <HelpCircle className="h-[19px] w-[19px] text-[#1E6F4F]" strokeWidth={1.7} />
            </div>
            <h2 className="text-xl font-bold tracking-[-.01em] text-card-foreground">
              Early tester guide
            </h2>
          </div>
          <div className="space-y-3 text-sm text-content-foreground">
            <p>
              You're one of our early testers — thank you. Your feedback shapes what we build next.
              Here's what we'd love from you:
            </p>
            <ul className="list-disc pl-5 space-y-2">
              <li>
                <strong>Use the app like you would in real life.</strong> Submit work packages,
                browse your programme, try the review flow end-to-end.
              </li>
              <li>
                <strong>Tell us when something breaks.</strong> If the app crashes, shows an error,
                or behaves unexpectedly, use the floating bug button in the bottom-right corner — it
                captures a screenshot and session replay so we can reproduce it.
              </li>
              <li>
                <strong>Tell us when something is confusing.</strong> If you're not sure what a
                button does, or the wording is unclear, that's valuable feedback. Use the form below
                to let us know.
              </li>
              <li>
                <strong>Share ideas.</strong> Missing feature? Workflow that would save you time?
                Drop it in as a feature request.
              </li>
            </ul>
            <p className="text-content-muted">
              We read every submission. We'll follow up by email when we have news on your specific
              feedback, but please allow a few days — we're a small team.
            </p>
          </div>
        </section>

        {/* Bug Report Callout */}
        <section className="rounded-[14px] border border-content-border bg-card px-7 py-[26px]">
          <div className="flex items-start gap-4">
            <div className="flex h-[42px] w-[42px] flex-none items-center justify-center rounded-[11px] bg-[#FBF2EE]">
              <Bug className="h-[19px] w-[19px] text-[#B0563C]" strokeWidth={1.7} />
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-bold tracking-[-.01em] text-card-foreground mb-1">
                Found a bug?
              </h2>
              <p className="text-sm text-content-muted">
                If something crashed or looks broken, use the floating bug button in the
                bottom-right corner of the screen. It captures a screenshot, console errors, and a
                session replay automatically — much faster than describing it in words.
              </p>
            </div>
          </div>
        </section>

        {/* Question / Feature Request Form */}
        <section className="rounded-[14px] border border-content-border bg-card px-7 py-[26px]">
          <h2 className="text-xl font-bold tracking-[-.01em] text-card-foreground mb-1">
            Ask a question or request a feature
          </h2>
          <p className="text-sm text-content-muted mb-6">
            For anything that isn't a bug — questions, suggestions, general feedback.
          </p>

          {submitted ? (
            <div className="flex flex-col items-center text-center py-8">
              <div className="mb-4 rounded-full bg-[#E9F1E6] p-4">
                <CheckCircle2 className="w-10 h-10 text-[#1E6F4F]" />
              </div>
              <h3 className="text-lg font-semibold text-content-foreground mb-2">
                Thanks! We've received your feedback
              </h3>
              <p className="text-sm text-content-muted mb-6 max-w-md">
                We'll get back to you soon. In the meantime, feel free to keep exploring the app.
              </p>
              <button
                type="button"
                onClick={resetForm}
                className="text-sm text-primary hover:underline"
              >
                Submit another
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Type */}
              <div>
                <label className="block text-sm font-medium text-content-foreground mb-2">
                  What's this about?
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {TYPE_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setType(opt.value)}
                      className={cn(
                        "rounded-[11px] border p-3.5 text-left transition-colors",
                        type === opt.value
                          ? "border-brand bg-[#F0F8F2]"
                          : "border-[#ECE8DA] bg-[#FDFBF4] hover:border-brand/50"
                      )}
                    >
                      <div className="font-medium text-sm text-content-foreground">{opt.label}</div>
                      <div className="text-xs text-content-muted mt-1">{opt.description}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Subject */}
              <div>
                <label
                  htmlFor="support-subject"
                  className="block text-sm font-medium text-content-foreground mb-2"
                >
                  Subject
                </label>
                <input
                  id="support-subject"
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  maxLength={SUBJECT_MAX}
                  placeholder="Short summary of your question or idea"
                  className={cn(
                    "w-full rounded-[9px] border bg-card px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30",
                    fieldErrors.subject ? "border-red-500" : "border-content-border"
                  )}
                  disabled={submitting}
                />
                {fieldErrors.subject && (
                  <p className="text-xs text-red-600 mt-1">{fieldErrors.subject}</p>
                )}
              </div>

              {/* Message */}
              <div>
                <label
                  htmlFor="support-message"
                  className="block text-sm font-medium text-content-foreground mb-2"
                >
                  Message
                </label>
                <textarea
                  id="support-message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  maxLength={MESSAGE_MAX}
                  rows={6}
                  placeholder="Tell us what's on your mind"
                  className={cn(
                    "w-full resize-y rounded-[9px] border bg-card px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30",
                    fieldErrors.message ? "border-red-500" : "border-content-border"
                  )}
                  disabled={submitting}
                />
                <div className="flex justify-between items-center mt-1">
                  {fieldErrors.message ? (
                    <p className="text-xs text-red-600">{fieldErrors.message}</p>
                  ) : (
                    <span />
                  )}
                  <span className="text-xs text-content-muted">
                    {message.length}/{MESSAGE_MAX}
                  </span>
                </div>
              </div>

              {error && (
                <div className="rounded-[9px] border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                  {error}
                </div>
              )}

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex items-center gap-2 rounded-[10px] bg-[#013220] px-5 py-3 text-[13px] font-bold text-[#F2EFE3] transition-colors hover:bg-[#0A4530] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Submit
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </section>

        {/* The user's submitted requests and any admin replies */}
        <MyRequests />

        {/* Contact */}
        <div className="flex flex-wrap items-center gap-6 rounded-[14px] bg-[#013220] px-8 py-[30px] text-[#F2EFE3]">
          <div className="min-w-[260px] flex-1">
            <h2 className="text-[22px] font-bold tracking-[-.01em]">
              Still stuck? We're quick to reply.
            </h2>
            <p className="mt-2 max-w-[520px] text-sm leading-[1.65] text-[#F2EFE3]/75">
              Email us and a real person will get back to you within one business day.
            </p>
          </div>
          <div className="flex flex-col items-start gap-2.5">
            <a
              href="mailto:info@dominionfinancialreview.com"
              className="flex items-center gap-[9px] rounded-[9px] bg-[#F2EFE3] px-5 py-3 text-[13px] font-bold tracking-[.04em] text-[#013220] transition-colors hover:bg-white"
            >
              <Mail className="h-[15px] w-[15px]" strokeWidth={1.8} />
              Email support
            </a>
            <div className="font-serif text-[12.5px] italic text-[#F2EFE3]/60">
              info@dominionfinancialreview.com
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
